import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Container, Paper, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HeaderApp from "../../components/HeaderApp";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getCursosActivosConPreguntas } from "../../services/cursosService";
import { useAuth } from "../../context/AuthContext";
import { logAxiosError } from "../../utils/logAxiosError";
import logoBioplastic from "../../assets/icons/logoPoliETITC.png";

const formatDate = (value) => {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) return new Date().toLocaleDateString("es-CO");
  return d.toLocaleDateString("es-CO", { year: "numeric", month: "long", day: "numeric" });
};

const getNombreCompleto = (user) =>
  [
    user?.PrimerNombre ?? user?.nombre ?? user?.Nombre ?? "",
    user?.PrimerApellido ?? user?.apellido ?? user?.Apellido ?? "",
  ]
    .join(" ")
    .trim() || "Estudiante";

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const drawWrappedText = (ctx, text, x, y, maxWidth, lineHeight) => {
  const words = String(text || "").split(" ");
  const lines = [];
  let line = "";
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  lines.forEach((l, idx) => ctx.fillText(l, x, y + idx * lineHeight));
  return lines.length;
};

const StudentCourseCertificate = () => {
  const { idCurso } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const canvasRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [dataCertificado, setDataCertificado] = useState({
    nombreCurso: location.state?.nombreCurso || "Curso",
    fechaFinalizacion: location.state?.fechaFinalizacion || new Date().toISOString(),
    nombreCompleto: location.state?.nombreCompleto || getNombreCompleto(user),
    mensaje: "¡Felicitaciones por completar satisfactoriamente este curso!",
  });

  useEffect(() => {
    let cancel = false;
    const run = async () => {
      try {
        setLoading(true);
        const list = await getCursosActivosConPreguntas();
        if (cancel) return;
        const cursos = Array.isArray(list) ? list : list?.data ?? [];
        const curso = cursos.find(
          (c) =>
            String(c?.idCurso ?? c?.id ?? c?.IdCurso) === String(idCurso)
        );
        setDataCertificado((prev) => ({
          ...prev,
          nombreCurso: curso?.Nombre ?? curso?.nombre ?? prev.nombreCurso,
          fechaFinalizacion: curso?.FechaFinalizacion ?? curso?.fechaFinalizacion ?? prev.fechaFinalizacion,
          nombreCompleto: prev.nombreCompleto || getNombreCompleto(user),
        }));
      } catch (e) {
        logAxiosError(`Certificado: cargar curso ${idCurso}`, e);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    run();
    return () => {
      cancel = true;
    };
  }, [idCurso, user]);

  const texto = useMemo(
    () => ({
      titulo: "CERTIFICADO DE FINALIZACIÓN DEL CURSO",
      curso: dataCertificado.nombreCurso,
      fecha: formatDate(dataCertificado.fechaFinalizacion),
      nombre: dataCertificado.nombreCompleto,
      mensaje: dataCertificado.mensaje,
    }),
    [dataCertificado]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const width = 1600;
    const height = 1100;
    canvas.width = width;
    canvas.height = height;

    const draw = async () => {
      ctx.fillStyle = "#f6f7f4";
      ctx.fillRect(0, 0, width, height);

      ctx.fillStyle = "#8faf57";
      ctx.fillRect(0, 0, width, 42);
      ctx.fillRect(0, height - 42, width, 42);
      ctx.fillRect(0, 0, 42, height);
      ctx.fillRect(width - 42, 0, 42, height);

      ctx.strokeStyle = "#355f3f";
      ctx.lineWidth = 6;
      ctx.strokeRect(58, 58, width - 116, height - 116);

      ctx.strokeStyle = "#d5dbd0";
      ctx.lineWidth = 2;
      ctx.strokeRect(84, 84, width - 168, height - 168);

      ctx.strokeStyle = "#9ab17b";
      ctx.lineWidth = 1;
      for (let y = 120; y < height - 120; y += 16) {
        ctx.beginPath();
        ctx.moveTo(120, y);
        ctx.quadraticCurveTo(width / 2, y - 6, width - 120, y);
        ctx.stroke();
      }

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(140, 130, width - 280, height - 260);

      ctx.strokeStyle = "#e0e5dd";
      ctx.lineWidth = 2;
      ctx.strokeRect(140, 130, width - 280, height - 260);

      try {
        const logo = await loadImage(logoBioplastic);
        const logoW = 190;
        const logoH = 190;
        ctx.drawImage(logo, width / 2 - logoW / 2, 165, logoW, logoH);
      } catch {
        // Si no carga el logo, mantenemos el resto del certificado.
      }

      ctx.fillStyle = "#2b5d38";
      ctx.font = "700 52px Arial";
      ctx.textAlign = "center";
      ctx.fillText(texto.titulo, width / 2, 430);

      ctx.strokeStyle = "#cfd8c7";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(250, 460);
      ctx.lineTo(width - 250, 460);
      ctx.stroke();

      ctx.fillStyle = "#131a22";
      ctx.font = "700 44px Arial";
      drawWrappedText(ctx, texto.curso.toUpperCase(), width / 2, 530, 980, 54);

      ctx.fillStyle = "#334155";
      ctx.font = "600 32px Arial";
      ctx.fillText(texto.fecha, width / 2, 655);
      ctx.font = "500 24px Arial";
      ctx.fillText("FECHA DE FINALIZACIÓN", width / 2, 690);

      ctx.strokeStyle = "#cfd8c7";
      ctx.beginPath();
      ctx.moveTo(310, 720);
      ctx.lineTo(width - 310, 720);
      ctx.stroke();

      ctx.fillStyle = "#111827";
      ctx.font = "700 50px Arial";
      drawWrappedText(ctx, texto.nombre.toUpperCase(), width / 2, 790, 980, 56);

      ctx.fillStyle = "#2f6b45";
      ctx.font = "italic 700 36px Georgia";
      drawWrappedText(ctx, texto.mensaje, width / 2, 900, 1060, 44);

      const url = canvas.toDataURL("image/png");
      setDownloadUrl(url);
    };

    draw();
  }, [texto]);

  return (
    <>
      <HeaderApp />
      <Box component="main" sx={{ py: 3, px: 2, bgcolor: "background.default", minHeight: "calc(100vh - 64px)" }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate("/AvailableCourses")}>
              Volver a cursos
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              href={downloadUrl}
              download={`certificado-curso-${idCurso}.png`}
              disabled={!downloadUrl}
            >
              Descargar certificado
            </Button>
          </Box>

          {loading ? (
            <Box py={8} display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <Paper sx={{ p: 2, overflowX: "auto" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Vista previa del diploma
              </Typography>
              <Box
                component="img"
                src={downloadUrl}
                alt="Certificado del curso"
                sx={{ width: "100%", maxWidth: 1100, display: "block", mx: "auto", borderRadius: 1 }}
              />
            </Paper>
          )}
        </Container>
      </Box>
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </>
  );
};

export default StudentCourseCertificate;
